export default function BoundarySection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <strong>Boundary Disputes</strong> and Encroachments
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Older family homes often have outdated surveys, fences built over property lines, or neighbor disputes about boundaries. These <strong>property survey issues</strong> can kill traditional sales, but we buy houses with boundary problems and encroachment issues.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What Are Boundary Disputes?</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              A boundary dispute occurs when there's disagreement about where one property ends and another begins. This can happen because:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Outdated surveys:</strong> The last survey was done 40+ years ago with less precise methods</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Fence placement:</strong> Fences were built based on assumptions, not actual property lines</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Encroachments:</strong> Buildings, driveways, or structures cross property lines</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Easement disputes:</strong> Disagreements about rights to use portions of the property</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Adverse possession claims:</strong> Neighbors claiming ownership through long-term use</p>
              </li>
            </ul>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              When you order a new survey to sell the property, these issues surface. The surveyor discovers the fence is 3 feet over the line, or the neighbor's shed is partially on your property, or your driveway encroaches on the neighbor's land.
            </p>
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-2">Why Traditional Buyers Walk Away:</p>
              <p className="text-gray-700">
                Mortgage lenders require clear boundaries and no encroachments. If the survey shows problems, they won't approve the loan. The buyer walks away, and you're stuck with an unsellable property.
              </p>
            </div>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/3b7503fd6fcd9d96d766042eaf8d2e9c.jpg"
              alt="Property surveyor measuring boundary lines"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 md:p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Common Encroachment Problems
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Building Encroachments</h4>
              <p className="text-gray-700 leading-relaxed">
                Part of the house, garage, or shed crosses the property line. This is the most serious type of encroachment and can require expensive legal action to resolve.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-roadster-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Driveway Encroachments</h4>
              <p className="text-gray-700 leading-relaxed">
                The driveway extends onto the neighbor's property or vice versa. Common in older neighborhoods where driveways were poured without surveys.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-layout-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Fence Line Disputes</h4>
              <p className="text-gray-700 leading-relaxed">
                The fence doesn't match the actual property line. One party may have been using land that doesn't belong to them for decades.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-plant-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Landscaping Encroachments</h4>
              <p className="text-gray-700 leading-relaxed">
                Trees, bushes, or landscaping features cross property lines. While less serious, these can still complicate sales and create neighbor conflicts.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-water-flash-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Utility Encroachments</h4>
              <p className="text-gray-700 leading-relaxed">
                Utility lines, septic systems, or wells that cross property boundaries. These can create ongoing access and maintenance issues.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-road-map-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Easement Disputes</h4>
              <p className="text-gray-700 leading-relaxed">
                Disagreements about who has the right to use driveways, pathways, or access routes across the property. Common in rural areas.
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=two%20neighbors%20arguing%20over%20wooden%20fence%20property%20line%20with%20survey%20map%20and%20measuring%20tape%20one%20pointing%20at%20fence%20other%20holding%20documents%20tense%20confrontation%20residential%20backyard%20boundary%20dispute&width=800&height=600&seq=titleissues009&orientation=landscape"
              alt="Neighbors disputing property boundary and fence line"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Quiet Title Actions for Boundary Issues</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              When boundary disputes can't be resolved through negotiation, you may need a <strong>quiet title action</strong>. This is a lawsuit that asks the court to determine the true property boundaries and "quiet" any competing claims.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Quiet title actions are:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Expensive:</strong> $5,000-$15,000+ in attorney fees</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Slow:</strong> 6-18 months to complete</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Uncertain:</strong> Court decisions can be unpredictable</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Adversarial:</strong> Destroys neighbor relationships permanently</p>
              </li>
            </ul>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Even after winning a quiet title action, you still need to find a buyer willing to purchase the property. You've spent months and thousands of dollars just to get to the starting line.
            </p>
            <div className="bg-emerald-500 text-white p-6 rounded-lg">
              <h4 className="font-bold text-xl mb-3">We Buy Properties with Boundary Issues</h4>
              <p className="leading-relaxed">
                As <strong>cash home buyers in El Paso</strong>, we can purchase properties with unresolved boundary disputes. We're willing to take on the "boundary risk" that traditional buyers and mortgage lenders won't accept. We'll handle the quiet title action ourselves after closing, or work with neighbors to resolve disputes.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why We Can Buy Properties Traditional Buyers Reject
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-6">Traditional Buyer Requirements:</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Clear Survey Required</p>
                    <p className="text-gray-700 text-sm">Mortgage lenders require a current survey showing no encroachments or boundary issues</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Title Insurance Required</p>
                    <p className="text-gray-700 text-sm">Title companies won't insure properties with known boundary disputes</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">No Risk Tolerance</p>
                    <p className="text-gray-700 text-sm">Buyers using mortgages can't accept any uncertainty about property boundaries</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Long Timeline</p>
                    <p className="text-gray-700 text-sm">Buyers expect you to resolve all issues before closing, which can take years</p>
                  </div>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-6">Our Approach:</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Cash Purchase</p>
                    <p className="text-gray-700 text-sm">No mortgage means no lender requirements. We can close with boundary issues present</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Risk Acceptance</p>
                    <p className="text-gray-700 text-sm">We're experienced investors who understand and accept boundary risks</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Post-Closing Resolution</p>
                    <p className="text-gray-700 text-sm">We handle quiet title actions and neighbor negotiations after we buy</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Fast Closing</p>
                    <p className="text-gray-700 text-sm">Close in 7-14 days instead of waiting months or years for resolution</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-lg">
            <p className="text-gray-700 leading-relaxed">
              <strong>Bottom line:</strong> We buy houses with boundary disputes, encroachments, easement issues, and survey problems. We understand that <strong>fixing title defects</strong> takes time and money, and we're willing to take on that burden so you don't have to. Call us at <strong>(915) 206-5814</strong> for a free consultation about your boundary issue.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}